import { createHttpClient } from '@/services/http/httpClient';
import { createAuthHeadersMiddleware } from '@/services/http/middleware/authHeaders';
import { createNetworkRetryMiddleware } from '@/services/http/middleware/networkRetry';
import { createTokenRefreshMiddleware } from '@/services/http/middleware/tokenRefresh';
import { createAbortMiddleware } from '@/services/http/middleware/abortController';
import CryptoJS from 'crypto-js';
import { graphqlClients } from '@/plugins/graphql';

export const HttpClientVuexPlugin = store => {
  const { middleware: abortMiddleware, abort } = createAbortMiddleware();

  const client = createHttpClient({
    baseURL: import.meta.env.VITE_BASE_API_URL,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    },
    middleware: [
      // Order matters: outer → inner. Retries by tokenRefresh re-enter authHeaders
      // so they pick up the freshly refreshed X-Api-Token from store.
      createNetworkRetryMiddleware({ maxRetries: 2 }),
      createTokenRefreshMiddleware({
        refreshFn: async ({ partnerHash, partnerId, partnerClientId }) => {
          await store.$refreshXApiToken(
            // $refreshXApiToken expects axios response format {status, data} — wrap $http
            {
              post: async (url, data, config) => {
                const result = await store.$http.post(url, data, config);
                return { status: 200, data: result };
              }
            },
            { partnerId, partnerHash, partnerClientId },
            () => {
              // Update GraphQL clients s novým tokenem
              Object.keys(graphqlClients).forEach(clientKey =>
                graphqlClients[clientKey].setHeader('X-Api-Token', store.state.apiToken.token)
              );
            }
          );
        },
        onLogout: async () => {
          // Navigation now lives inside auth/logout (router.replace on mobile,
          // location.href on web via useLogoutRedirect). Don't call location.href
          // here — it would race with the redirect inside the action and on web
          // would also fire before auth/reset completes.
          await store.dispatch('auth/logout');
        },
        // Evaluated per-request inside tokenRefresh so a snapshot taken at
        // app boot (when isLoggedIn is false) doesn't permanently disable
        // session-expiry detection.
        isLoggedIn: () => store.state.auth.isLoggedIn,
        isMobileApp: () => store.state.general.isMobileApp,
        getPrivateKey: isMobile => {
          const privateKey = isMobile
            ? import.meta.env.PRIVATE_KEY_APP_APP
            : import.meta.env.PRIVATE_KEY + Math.floor(Date.now() / 60);
          return CryptoJS.MD5(privateKey).toString();
        },
        getPartnerClientId: isMobile =>
          isMobile
            ? import.meta.env.VITE_PARTNER_CLIENT_ID_APP_APP
            : import.meta.env.VITE_PARTNER_CLIENT_ID,
        baseURL: import.meta.env.VITE_BASE_API_URL
      }),
      createAuthHeadersMiddleware({
        apiKey: import.meta.env.VITE_API_KEY_APP,
        getApiToken: () => store.state.apiToken.token,
        getAccessToken: () => store.state.auth.accessToken,
        shouldSendAuth: () => store.state.general.sendAuthorizationHeader
      }),
      abortMiddleware
    ]
  });

  store.$http = client;
  store.$abortRequests = abort;
};
